'use client';

import { useLayoutEffect } from 'react';
import type { AuthThemeId } from '@/lib/auth-branding';
import {
  applyContrast,
  getStoredContrast,
} from '@/components/tenant/theme/contrast-options';

/**
 * Aplica o tema das telas públicas (login, register, forgot/reset password,
 * verify email, pricing, /booking/<slug>) sem FOUC.
 *
 * Estratégia DB-first via subdomínio:
 *  - O tema vem resolvido server-side pelo slug do tenant (Tenant.settings.loginTheme)
 *    ou pela config da plataforma (PlatformConfig.loginTheme). O DB é a fonte
 *    de verdade; SSR já entrega o tema correto antes do paint.
 *  - A preferencia do servidor sempre vence. Tema não usa localStorage como
 *    fonte de verdade nem como fallback funcional.
 *  - `7cors-system`: manter a preferencia em data-theme-preference e aplicar
 *    em data-theme o tema efetivo persistido no PostgreSQL.
 *  - A propagação verdadeira é DB-first via slug/config da plataforma.
 *
 * Dois mecanismos combinados pra cobrir SSR e SPA navigation:
 *  1) `<script>` inline aplica o `data-theme` ANTES do primeiro paint -
 *     evita FOUC em full reload (F5, abrir aba nova, direct URL).
 *  2) `useLayoutEffect` reaplica em CADA mount/update do client component -
 *     cobre SPA navigation (ex: logout vindo de `/admin` em tema dark
 *     navega pra `/login` via router.push; o `<html data-theme="7cors-dark">`
 *     antigo sobreviveria sem isso, ja que o `<script>` inline so roda no
 *     SSR. Reaplicar via effect garante que /login sempre mostra o tema
 *     do PlatformConfig.loginTheme persistido no banco, independente de
 *     onde o user veio).
 */
export function AuthThemeApplier({
  theme,
  effectiveTheme,
}: {
  theme: AuthThemeId;
  effectiveTheme?: AuthThemeId | null;
}) {
  useLayoutEffect(() => {
    if (typeof document === 'undefined') return;
    const domTheme =
      theme === '7cors-system' && effectiveTheme ? effectiveTheme : theme;
    document.documentElement.dataset.themePreference = theme;
    document.documentElement.dataset.theme = domTheme;
    applyContrast(getStoredContrast());
  }, [theme, effectiveTheme]);

  const code = `(function(){try{
    var serverTheme=${JSON.stringify(theme)};
    var serverEffectiveTheme=${JSON.stringify(effectiveTheme ?? null)};
    var apply=function(theme){
      var domTheme=theme==='7cors-system'&&serverEffectiveTheme?serverEffectiveTheme:theme;
      document.documentElement.dataset.themePreference=theme;
      document.documentElement.dataset.theme=domTheme;
    };
    apply(serverTheme);
    var c=null;try{c=localStorage.getItem('7barber-contrast');}catch(e){}
    if(c==null&&window.matchMedia&&window.matchMedia('(prefers-contrast: more)').matches){c='more';}
    if(c==='more'){document.documentElement.dataset.contrast='more';}
    else{delete document.documentElement.dataset.contrast;}
  }catch(e){}})();`;
  return <script dangerouslySetInnerHTML={{ __html: code }} />;
}
